import React, { useState } from 'react';
import { Link } from "react-router-dom";

function WebDevelopment() {
  const [activeTab, setActiveTab] = useState('frontend');

  const webDevServices = [
    {
      icon: "🌐",
      title: "Frontend Development",
      description: "Modern, responsive user interfaces with React, Next.js, and Vue.js",
      features: ["React/Next.js", "TypeScript", "Tailwind CSS", "Responsive Design"]
    },
    {
      icon: "⚙️",
      title: "Backend Development",
      description: "Scalable server-side solutions with Node.js, Python, and .NET",
      features: ["Node.js/Express", "Python/Django", "API Development", "Microservices"]
    },
    {
      icon: "📱",
      title: "Progressive Web Apps",
      description: "Native-like web applications with offline capabilities",
      features: ["PWA", "Service Workers", "Push Notifications", "App-like UI"]
    },
    {
      icon: "🛒",
      title: "E-commerce Solutions",
      description: "Complete online store platforms with payment integration",
      features: ["Shopify", "WooCommerce", "Custom Stores", "Payment Gateways"]
    },
    {
      icon: "📊",
      title: "CMS Development",
      description: "Custom content management systems tailored to your needs",
      features: ["WordPress", "Strapi", "Sanity", "Custom CMS"]
    },
    {
      icon: "💬",
      title: "Web Applications",
      description: "Complex business applications with real-time features",
      features: ["Real-time Apps", "Dashboard Systems", "Admin Panels", "CRM Systems"]
    }
  ];

  const techStack = {
    frontend: [
      { name: "React", level: "Advanced" },
      { name: "Next.js", level: "Advanced" },
      { name: "TypeScript", level: "Advanced" },
      { name: "Tailwind CSS", level: "Expert" },
      { name: "Vue.js", level: "Intermediate" },
      { name: "Redux", level: "Advanced" }
    ],
    backend: [
      { name: "Node.js", level: "Advanced" },
      { name: "Python", level: "Intermediate" },
      { name: "MongoDB", level: "Advanced" },
      { name: "PostgreSQL", level: "Intermediate" },
      { name: "GraphQL", level: "Advanced" },
      { name: "Redis", level: "Intermediate" }
    ],
    devops: [
      { name: "Docker", level: "Advanced" },
      { name: "Kubernetes", level: "Intermediate" },
      { name: "AWS", level: "Intermediate" },
      { name: "CI/CD", level: "Advanced" },
      { name: "Nginx", level: "Intermediate" },
      { name: "Linux", level: "Advanced" }
    ]
  };

  const webProjects = [
    {
      id: 1,
      title: "Enterprise SaaS Platform",
      description: "Cloud-based software for managing enterprise resources with real-time analytics",
      category: "Web Application",
      tech: ["React", "Node.js", "MongoDB", "AWS"],
      results: ["40% faster load times", "99.9% uptime", "500% user growth"]
    },
    {
      id: 2,
      title: "E-commerce Marketplace",
      description: "Multi-vendor marketplace with advanced search and recommendation engine",
      category: "E-commerce",
      tech: ["Next.js", "TypeScript", "PostgreSQL", "Redis"],
      results: ["1M+ monthly visitors", "30% conversion rate", "24/7 availability"]
    },
    {
      id: 3,
      title: "Healthcare Portal",
      description: "Patient management system with telemedicine and appointment scheduling",
      category: "Healthcare",
      tech: ["React", "Python", "Docker", "Azure"],
      results: ["HIPAA compliant", "50k+ users", "99.9% reliability"]
    }
  ];

  const processSteps = [
    {
      step: "01",
      title: "Discovery & Planning",
      description: "We analyze requirements and create detailed project specifications",
      icon: "📋"
    },
    {
      step: "02",
      title: "UI/UX Design",
      description: "Creating intuitive user interfaces and seamless user experiences",
      icon: "🎨"
    },
    {
      step: "03",
      title: "Development",
      description: "Agile development with regular demos and iterations",
      icon: "💻"
    },
    {
      step: "04",
      title: "Testing & QA",
      description: "Comprehensive testing including unit, integration, and user testing",
      icon: "🔍"
    },
    {
      step: "05",
      title: "Deployment",
      description: "Seamless deployment with CI/CD pipelines and monitoring",
      icon: "🚀"
    },
    {
      step: "06",
      title: "Maintenance",
      description: "Ongoing support, updates, and performance optimization",
      icon: "🔄"
    }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* ===== HERO SECTION ===== */}
      <section className="relative overflow-hidden bg-gradient-to-b from-gray-50 to-white py-20 md:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* LEFT CONTENT */}
            <div className="text-left">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                <span className="text-gray-900">
                  Modern Web
                </span>
                <br />
                <span className="mt-2 inline-block text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-gray-800 to-blue-600">
                  Development Solutions
                </span>
              </h1>

              <p className="mt-6 text-xl md:text-2xl text-gray-600 max-w-3xl leading-relaxed">
                We build high-performance, scalable web applications that drive business growth
                and deliver exceptional user experiences with clear ownership and accountability.
              </p>

              
            </div>

            {/* RIGHT IMAGE */}
            <div className="flex justify-center lg:justify-end">
              <img
                src="/web.png"
                alt="Web Development"
                className="w-full max-w-lg rounded-xl shadow-xl"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20">
            <div className="bg-gray-50 border border-gray-200 p-6 rounded-xl hover:border-red-300 hover:shadow-lg transition-all">
              <div className="text-red-600 mb-3 text-2xl">⌛</div>
              <div className="text-3xl font-bold text-gray-900 mb-2">24/7</div>
              <div className="text-gray-600">Support</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 p-6 rounded-xl hover:border-red-300 hover:shadow-lg transition-all">
              <div className="text-red-600 mb-3 text-2xl">📈</div>
              <div className="text-3xl font-bold text-gray-900 mb-2">99.9%</div>
              <div className="text-gray-600">Uptime</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 p-6 rounded-xl hover:border-red-300 hover:shadow-lg transition-all">
              <div className="text-red-600 mb-3 text-2xl">👥</div>
              <div className="text-3xl font-bold text-gray-900 mb-2">50+</div>
              <div className="text-gray-600">Web Projects</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 p-6 rounded-xl hover:border-red-300 hover:shadow-lg transition-all">
              <div className="text-red-600 mb-3 text-2xl">✅</div>
              <div className="text-3xl font-bold text-gray-900 mb-2">100%</div>
              <div className="text-gray-600">Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES SECTION ===== */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Our Web Development <span className="text-red-600">Expertise</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From simple websites to complex web applications, we deliver solutions that exceed expectations
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {webDevServices.map((service, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="text-4xl mb-6">{service.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-red-600 transition-colors">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-gray-700">
                      <div className="w-2 h-2 rounded-full bg-red-500 mr-3"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TECH STACK SECTION ===== */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Cutting-Edge <span className="text-red-600">Technology Stack</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We use modern technologies to build robust and scalable web solutions
            </p>
          </div>
          
          <div className="mb-8">
            <div className="flex flex-wrap gap-4 justify-center mb-8">
              <button 
                onClick={() => setActiveTab('frontend')}
                className={`px-6 py-3 rounded-lg font-medium transition ${
                  activeTab === 'frontend' 
                    ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Frontend
              </button>
              <button 
                onClick={() => setActiveTab('backend')}
                className={`px-6 py-3 rounded-lg font-medium transition ${
                  activeTab === 'backend' 
                    ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Backend
              </button>
              <button 
                onClick={() => setActiveTab('devops')}
                className={`px-6 py-3 rounded-lg font-medium transition ${
                  activeTab === 'devops' 
                    ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                DevOps
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {techStack[activeTab].map((tech, index) => (
                <div 
                  key={index}
                  className="bg-gray-50 border border-gray-200 p-6 rounded-xl hover:border-red-300 hover:shadow-lg transition-all text-center"
                >
                  <div className="text-lg font-bold text-gray-900 mb-2">
                    {tech.name}
                  </div>
                  <div className={`text-sm font-medium ${
                    tech.level === 'Expert' ? 'text-green-600' :
                    tech.level === 'Advanced' ? 'text-blue-600' :
                    'text-amber-600'
                  }`}>
                    {tech.level}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== DEVELOPMENT PROCESS SECTION ===== */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Our Development <span className="text-red-600">Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A structured approach that ensures quality, transparency, and timely delivery
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
            {processSteps.map((step, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="text-3xl font-bold text-gray-300">
                    {step.step}
                  </div>
                  <div className="text-2xl">
                    {step.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROJECTS SHOWCASE SECTION ===== */}
      <section className="py-20 bg-white" id="projects">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Featured <span className="text-red-600">Projects</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how we've helped businesses transform their digital presence
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {webProjects.map((project) => (
              <div 
                key={project.id}
                className="bg-gray-50 border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="p-8">
                  <div className="inline-flex items-center gap-2 bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-medium mb-4">
                    {project.category}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{project.title}</h3>
                  <p className="text-gray-600 mb-6">{project.description}</p>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Technologies:</h4>
                    <div className="flex flex-wrap gap-2">
                      {project.tech.map((tech, idx) => (
                        <span key={idx} className="bg-white px-3 py-1 rounded-lg text-sm text-gray-700 border border-gray-300">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Results:</h4>
                    <ul className="space-y-2">
                      {project.results.map((result, idx) => (
                        <li key={idx} className="flex items-center text-gray-700">
                          <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                          {result}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Ready to Build Your <span className="text-red-400">Web Solution</span>?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Let's discuss your project requirements and create a tailored web development strategy
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-red-600 to-red-700 text-white px-10 py-5 rounded-xl font-semibold text-lg shadow-2xl hover:shadow-red-500/30 hover:scale-[1.02] transition-all duration-300 group"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
              Start Your Project Now
              <svg className="w-5 h-5 transform group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
              </svg>
            </Link>
            
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-3 bg-transparent text-white border-2 border-gray-700 px-10 py-5 rounded-xl font-semibold text-lg hover:border-red-500 hover:bg-red-500/10 transition-all duration-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
              </svg>
              Book a Free Consultation
            </Link>
          </div>
          
          <p className="mt-10 text-gray-400">
            No templates. No shortcuts. Just custom web solutions built for your success.
          </p>
        </div>
      </section>
    </div>
  );
}

export default WebDevelopment;